import { useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import { alpha, useTheme } from '@mui/material/styles';

import { createPortalSession } from 'src/redux/slices/subscription';

import Iconify from '../iconify';

// ----------------------------------------------------------------------

export default function ReactivateAccess () {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleUpdatePayment = async () => {
    try {
      setLoading(true);
      setError(null);

      // Open Stripe Customer Portal
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (err) {
      console.error('Failed to open customer portal:', err);
      setError("Couldn't open the payment page. Try again, or contact support.");
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          py: 8,
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Card
          sx={{
            p: 5,
            width: '100%',
            maxWidth: 600,
            boxShadow: theme.customShadows.z24,
            border: `2px solid ${alpha(theme.palette.error.main, 0.08)}`,
          }}
        >
          <Stack spacing={4} alignItems="center">
            {/* Warning Icon with Animation */}
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.12)} 0%, ${alpha(theme.palette.warning.main, 0.12)} 100%)`,
                position: 'relative',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': {
                    transform: 'scale(1)',
                  },
                  '50%': {
                    transform: 'scale(1.05)',
                  },
                },
              }}
            >
              <Iconify
                icon="solar:shield-warning-bold-duotone"
                width={72}
                sx={{ color: 'error.main' }}
              />
            </Box>

            {/* Main Heading */}
            <Stack spacing={1.5} alignItems="center">
              <Typography variant="h3" sx={{ textAlign: 'center' }}>
                Your Access is Paused
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 480 }}
              >
                Your last payment didn&apos;t go through.
              </Typography>
            </Stack>

            {/* What You're Missing Out On */}
            <Box
              sx={{
                width: '100%',
                p: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.info.main, 0.08),
                border: `1px dashed ${alpha(theme.palette.info.main, 0.24)}`,
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Iconify icon="solar:clock-circle-bold" width={24} sx={{ color: 'info.main' }} />
                  <Typography variant="subtitle1" sx={{ color: 'info.main' }}>
                    Update your payment method to get back:
                  </Typography>
                </Stack>

                <Stack spacing={1.5} sx={{ pl: 4 }}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Iconify icon="solar:check-circle-bold" width={20} sx={{ color: 'success.main', mt: 0.3 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      All your <strong>listings</strong> and inventory
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Iconify icon="solar:check-circle-bold" width={20} sx={{ color: 'success.main', mt: 0.3 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Your <strong>saved templates</strong> and default policies
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Iconify icon="solar:check-circle-bold" width={20} sx={{ color: 'success.main', mt: 0.3 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      <strong>Cross-listing</strong> to multiple marketplaces
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Iconify icon="solar:check-circle-bold" width={20} sx={{ color: 'success.main', mt: 0.3 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      <strong>Automatic updates</strong> across your marketplaces
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Box>

            {/* Reassurance Message */}
            <Alert severity="success" sx={{ width: '100%' }}>
              <Typography variant="body2">
                <strong>Your listings and settings are safe.</strong> Update your payment method and you&apos;re straight back in.
              </Typography>
            </Alert>

            {/* Error Message */}
            {error && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
              </Alert>
            )}

            {/* Action Buttons */}
            <Stack spacing={2} sx={{ width: '100%', mt: 2 }}>
              <LoadingButton
                fullWidth
                size="large"
                variant="contained"
                color="primary"
                loading={loading}
                onClick={handleUpdatePayment}
                startIcon={<Iconify icon="solar:card-bold" />}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                }}
              >
                Update Payment Method
              </LoadingButton>

              <Button
                fullWidth
                size="large"
                variant="outlined"
                color="inherit"
                href="mailto:hello@voolist.com"
                startIcon={<Iconify icon="solar:chat-round-line-bold" />}
              >
                Contact Support
              </Button>
            </Stack>

            {/* Fine Print */}
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                textAlign: 'center',
                maxWidth: 400,
                mt: 2,
              }}
            >
              You&apos;ll update your payment details on a secure payment page.
            </Typography>
          </Stack>
        </Card>
      </Box>
    </Container>
  );
}

